import { useEffect, useState } from 'react'
import { api, ApiError } from '../api'
import type { Equipment, ReportTemplate } from '../api'

const EQUIPMENT_STORE = 'cscan.equipment'

// Porta de entrada do trabalho: escolher o paciente e o tipo de exame e
// começar. Daqui sai a captura e, no fim, o laudo — sempre nessa ordem.
// A agenda é opcional: o exame pode ser avulso.
export function IniciarExameButton({ label, primary, onStarted }: {
  label?: string
  primary?: boolean
  onStarted: (examId: string, patientId: string, patientName: string) => void
}) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button className={primary ? 'btn btn-primary' : 'btn'} onClick={() => setOpen(true)} style={{ height: 30, padding: '0 14px' }}>
        {label ?? 'Iniciar exame'}
      </button>
      {open && <IniciarExameModal onClose={() => setOpen(false)} onStarted={(i, p, n) => { setOpen(false); onStarted(i, p, n) }} />}
    </>
  )
}

export function IniciarExameModal({ onClose, onStarted }: {
  onClose: () => void
  onStarted: (examId: string, patientId: string, patientName: string) => void
}) {
  const [templates, setTemplates] = useState<ReportTemplate[]>([])
  const [query, setQuery] = useState('')
  const [options, setOptions] = useState<{ id: string; name: string }[]>([])
  const [patient, setPatient] = useState<{ id: string; name: string } | null>(null)
  const [examType, setExamType] = useState('')
  const [equipments, setEquipments] = useState<Equipment[]>([])
  const [equipmentId, setEquipmentId] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    void api.templates.list().then((t) => {
      setTemplates(t.items)
      if (t.items[0]) setExamType(t.items[0].name)
    }).catch(() => undefined)
    // aparelho: começa no último usado neste computador
    void api.catalog.equipments().then((out) => {
      setEquipments(out.items)
      let last = ''
      try { last = localStorage.getItem(EQUIPMENT_STORE) ?? '' } catch { /* sem preferência guardada */ }
      if (out.items.some((e) => e.id === last)) setEquipmentId(last)
    }).catch(() => undefined)
  }, [])

  useEffect(() => {
    const t = setTimeout(() => {
      void api.patients.list(query, 8).then((r) => setOptions(r.items.map((p) => ({ id: p.id, name: p.name })))).catch(() => undefined)
    }, 200)
    return () => clearTimeout(t)
  }, [query])

  async function start() {
    if (!patient) { setError('Selecione o paciente.'); return }
    if (!examType) { setError('Selecione o tipo de exame.'); return }
    setBusy(true)
    setError('')
    try {
      const out = await api.exams.create({ patientId: patient.id, type: examType, equipmentId: equipmentId || undefined })
      if (equipmentId) { try { localStorage.setItem(EQUIPMENT_STORE, equipmentId) } catch { /* vale só agora */ } }
      onStarted(out.exam.id, patient.id, patient.name)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'falha ao iniciar o exame')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(8,14,18,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60 }}>
      <div onClick={(e) => e.stopPropagation()} className="fade-in"
        style={{ width: 520, background: 'var(--surface)', border: '1px solid var(--border-2)', borderRadius: 9, boxShadow: 'var(--shadow-lg)', overflow: 'hidden' }}>
        <div style={{ padding: '13px 16px', borderBottom: '1px solid var(--border)', font: '600 14px/1.2 var(--font-sans)' }}>
          Iniciar exame
        </div>

        <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 11 }}>
          <label className="field">
            <span>Paciente</span>
            <input className="input" autoFocus placeholder="Buscar por nome, CPF ou documento…"
              value={patient?.name ?? query}
              onChange={(e) => { setPatient(null); setQuery(e.target.value) }}
              onKeyDown={(e) => { if (e.key === 'Enter') void start() }} />
          </label>
          {!patient && query.length > 0 && (
            <div style={{ border: '1px solid var(--border)', borderRadius: 6, maxHeight: 170, overflowY: 'auto' }}>
              {options.map((o) => (
                <div key={o.id} onClick={() => { setPatient(o); setQuery(o.name); setOptions([]) }}
                  style={{ padding: '8px 10px', cursor: 'pointer', borderBottom: '1px solid var(--border)', fontSize: 12.5 }}>
                  {o.name}
                </div>
              ))}
              {options.length === 0 && (
                <div style={{ padding: '8px 10px', fontSize: 11.5, color: 'var(--text-3)' }}>
                  Nenhum paciente encontrado. Cadastre em Pacientes.
                </div>
              )}
            </div>
          )}

          <label className="field">
            <span>Tipo de exame</span>
            <select className="input" value={examType} onChange={(e) => setExamType(e.target.value)}>
              {templates.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
            </select>
          </label>

          {equipments.length > 1 && (
            <label className="field">
              <span>Aparelho</span>
              <select className="input" value={equipmentId} onChange={(e) => setEquipmentId(e.target.value)}>
                <option value="">—</option>
                {equipments.map((e) => <option key={e.id} value={e.id}>{e.description}{e.serial ? ` · ${e.serial}` : ''}</option>)}
              </select>
            </label>
          )}

          {error && <div style={{ padding: '8px 11px', border: '1px solid var(--danger)', borderRadius: 6, background: 'var(--danger-soft)', fontSize: 12, color: 'var(--danger)' }}>{error}</div>}
        </div>

        <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', background: 'var(--surface-2)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn" onClick={onClose} style={{ height: 31 }}>Cancelar</button>
          <button className="btn btn-primary" onClick={() => void start()} disabled={busy} style={{ height: 31, padding: '0 18px' }}>
            {busy ? 'Iniciando…' : 'Iniciar exame → Captura'}
          </button>
        </div>
      </div>
    </div>
  )
}
